import { breakpoints, Inline, Stack, Text, vars } from '@etg/wings';
import type { CurrencyAmount } from '@eti/schema-types';
import { css } from '@eti/styles';
import { useProperty, useTranslation } from '@eti/providers';
import type { MutableRefObject } from 'react';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { getMobileDatesPrices } from '../utils/getMobileDatesPrices';
import type { NearbyDatePricesType } from '../../../../models';
import type { SearchFormValues } from '../../../../../../common/search-form-panel/SearchFormNewContainer';
import NearbyDatePrice from './NearbyDatePrice';

const nearbyDatesPricesStyles = (isLabelAtBottom: boolean) => css`
  border: 1px solid ${vars.colors.divider};
  border-radius: ${vars.button.borderRadius};
  width: 100%;
  ${!isLabelAtBottom && 'margin-bottom: 16px;'}
`;

const nearbyDatesPricesLabelStyles = (isLabelAtBottom: boolean) => css`
  color: ${vars.colors.promotional.foreground};
  ${isLabelAtBottom ? 'margin: 16px 0;' : 'margin-bottom: 16px;'}
`;

export const NearbyPricesWithoutSelectedTripVersionEnumTypes = {
  A: 'A',
  B: 'B',
  C: 'C',
} as const;
export type NearbyPricesWithoutSelectedTripVersionEnumTypesValues =
  (typeof NearbyPricesWithoutSelectedTripVersionEnumTypes)[keyof typeof NearbyPricesWithoutSelectedTripVersionEnumTypes];

export const NearbyPricesWithSelectedTripVersionEnumTypes = {
  A: 'A',
  B: 'B',
} as const;
export type NearbyPricesWithSelectedTripVersionEnumTypesValues =
  (typeof NearbyPricesWithSelectedTripVersionEnumTypes)[keyof typeof NearbyPricesWithSelectedTripVersionEnumTypes];

export type NearbyDatePriceType = {
  departureDate: string;
  isCheapest: boolean;
  isSelected: boolean;
  price: CurrencyAmount;
  returnDate?: string;
};

export type NearbyDatesPricesProps = {
  nearbyDatePrices: NearbyDatePricesType;
  handleSearch: (keepFilters: boolean) => void;
  hasSelectedTrip: boolean;
  isLoading: boolean;
  searchFormWatchRef: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  setValueRef: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
};

const NearbyDatesPrices = ({
  setValueRef,
  searchFormWatchRef,
  nearbyDatePrices,
  hasSelectedTrip,
  handleSearch,
  isLoading,
}: NearbyDatesPricesProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();

  const nearbyDatesPricesLabel = t('Result.NearbyPrices.Label');

  const NearbyPricesWithoutSelectedTripVersion: (typeof NearbyPricesWithoutSelectedTripVersionEnumTypes)[keyof typeof NearbyPricesWithoutSelectedTripVersionEnumTypes] =
    p('Result.Page.NearbyPrices.WithoutSelectedTrip.Version');

  const renderLabelAtBottom =
    !hasSelectedTrip &&
    (NearbyPricesWithoutSelectedTripVersion === NearbyPricesWithoutSelectedTripVersionEnumTypes.A ||
      NearbyPricesWithoutSelectedTripVersion === NearbyPricesWithoutSelectedTripVersionEnumTypes.B);

  const getDatesPrices = () => {
    const isMobileDevice = window.matchMedia(`(max-width: ${breakpoints._768})`).matches;

    return isMobileDevice ? getMobileDatesPrices(nearbyDatePrices) : nearbyDatePrices;
  };

  return (
    <Stack data-testid="nearby-dates-prices" spacing={0}>
      {!renderLabelAtBottom && (
        <Text
          as="div"
          className={nearbyDatesPricesLabelStyles(renderLabelAtBottom)}
          data-testid="nearby-dates-prices-label-at-top"
          variant="14-bold"
        >
          {nearbyDatesPricesLabel}
        </Text>
      )}
      <Inline className={nearbyDatesPricesStyles(renderLabelAtBottom)} noWrap spacing={0}>
        {getDatesPrices().map((datePrice, index) => (
          <NearbyDatePrice
            departureDate={datePrice?.departureDate ?? ''}
            handleSearch={handleSearch}
            isCheapest={datePrice?.isCheapest ?? false}
            isLoading={isLoading}
            isSelected={datePrice?.isSelected ?? false}
            key={index}
            price={datePrice?.price}
            returnDate={datePrice?.returnDate}
            searchFormWatchRef={searchFormWatchRef}
            setValueRef={setValueRef}
          />
        ))}
      </Inline>
      {renderLabelAtBottom && (
        <Text
          as="div"
          className={nearbyDatesPricesLabelStyles(renderLabelAtBottom)}
          data-testid="nearby-dates-prices-label-at-bottom"
          variant="14-bold"
        >
          {nearbyDatesPricesLabel}
        </Text>
      )}
    </Stack>
  );
};
export default NearbyDatesPrices;
